<template>
  <Modal :style="computedWindowSize"
         :visible="visible"
         class="p-fluid"
         @close="close">
    <template v-slot:header>
      <div class="p-dialog-title">{{ $t( 'supplierReturnsLabel' )}}</div>
    </template>
    <div class="p-pb-4">
      <Toolbar>
        <template #start>
          <MultipleDeleteButton :label="`${$t('Delete')} ${selectedItems?.length ? `(${selectedItems?.length})`  : ''}`"
                                @click="confirmItemsMultipleDelete"
                                :disabled="dataTableIsLoading || !selectedItems?.length"/>
        </template>
        <template #end>
          <div class="p-d-flex p-ai-center p-mr-2">
            <Checkbox id="reduced-positions-last-30-days"
                      :binary="true"
                      :disabled="dataTableIsLoading"
                      v-model="showOnlyLast30Days"
                      @change="changeLast30DaysFilter"/>
            <label for="reduced-positions-last-30-days" class="pointer p-p-1">
              {{ $t('Last 30 Days') }}
            </label>
          </div>
          <SearchInput :changeUrl="false" ref="searchInput" @search-items="getItems" :disabled="dataTableIsLoading || disableSearchInput" v-model="searchData" @show-spinner="isLoading = true"/>
        </template>
      </Toolbar>
    </div>


    <Spinner v-if="isLoading"/>
    <DesktopDataTable :dataTableIsLoading="dataTableIsLoading"
                      v-else
                      :emptyBlockStyle="'padding: 8vh 0;'"
                      :itemsArrayIsEmpty="itemsArrayIsEmpty"
                      :sort="sort"
                      :showSearchInput="false"
                      :actionButtonsCount="actionButtonsCount"
                      @sort-items="sortItems"
                      :headers="[
                        {name: 'code', title: $t('Code'), sortable: false},
                        {name: 'name', title: $t('Name'), sortable: false},
                        {name: 'supplier', title: $t('Supplier'), sortable: false},
                        {name: 'qty', title: $t('Qty'), sortable: false},
                        {name: 'purchase_price', title: $t('purchasePriceShortLabel'), sortable: false},
                        // {name: 'sell_price', title: 'Sell price', sortable: false},
                        {name: 'order', sortable: false},
                        {name: 'type', title: $t('Type'), sortable: false},
                        {name: 'reason', title: $t('Reason'), sortable: false},
                        {name: 'comment', sortable: false},
                        {name: 'created_at', title: $t('Created'), sortable: true},
                      ]">
      <template v-slot:multiselectHeader>
        <th v-show="$store.getters.isAccountantOrHigher" class="multiSelect-column-header">
          <Checkbox :disabled="allCurrentItemsAreAdded || dataTableIsLoading" :class="{ 'no-pointer':allCurrentItemsAreAdded}" @click="selectAllCheckboxOnClick" :value="0" v-model="selectAllCheckbox"/>
        </th>
      </template>
      <template v-slot:body>
        <tr v-for="(item, index) of items"
            :key="index"
            :class="{'p-highlight': isItemSelected(item.id)}">
          <td :class="{ 'new-item': isItemAdded(item.id) }" v-show="$store.getters.isAccountantOrHigher">
            <Checkbox :disabled="dataTableIsLoading || isItemAdded(item.id)" :class="{ 'no-pointer':isItemAdded(item.id)}" :value="item.id" v-model="selectedItems"/>
          </td>
          <td :class="{ 'new-item': isItemAdded(item.id) }">
            <span v-if="item.position_data?.code">{{ item.position_data.code }}</span>
            <i v-else class="ti-minus"></i>
          </td>
          <td :class="{ 'new-item': isItemAdded(item.id) }">
            <span v-if="item.position_data">{{ item.position_data.name }}</span>
          </td>
          <td :class="{ 'new-item': isItemAdded(item.id) }">
            <span v-if="item.supplier">{{ item.supplier.company_name }}</span>
          </td>
          <td :class="{ 'new-item': isItemAdded(item.id) }">
            <div style="position:relative; left:10px">{{ +item.qty - +item.fixed_qty }}</div>
          </td>
          <td :class="{ 'new-item': isItemAdded(item.id) }">
            <span v-if="item.position_data?.purchase_price">{{ formatCurrency(item.position_data.purchase_price) }}</span>
            <i v-else class="ti-minus"/>
          </td>
          <td :class="{ 'new-item': isItemAdded(item.id) }">
<!--            <span v-if="item.order?.uid" class="p-d-flex p-ai-center">-->
<!--              <router-link :to="{ path: `/orders/${item.order.uid}` }" target="_blank" draggable="false">-->
<!--                <Button class="p-button-text p-p-1" v-tooltip.top="item.order?.number ? item.order.number : null" :class="{'highlight&#45;&#45;link':isItemSelected(item.id)}">-->
<!--                  <i class="ti-hummer"/>-->
<!--                </Button>-->
<!--              </router-link>-->
<!--            </span>-->
<!--            <i v-else class="ti-minus"/>-->
            <OpenOrderButton :position="item"/>
          </td>
          <td :class="{ 'new-item': isItemAdded(item.id) }">
            <span v-if="item.type">{{ ReducedPositionTypes.find(type => type.id === item.type)?.label[$i18n.locale] }}</span>
            <i v-else class="ti-minus"/>
          </td>
          <td :class="{ 'new-item': isItemAdded(item.id) }">
            <span v-if="item.reason">{{ ReducedPositionReasons.find(reason => reason.id === item.reason)?.label[$i18n.locale] }}</span>
            <router-link v-else-if="item.purchaseInvoice && item.purchaseInvoice.uid && item.purchaseInvoice.number"
                         :to="{ path: '/purchase-invoices', query: { show: item.purchaseInvoice.uid }}"
                         target="_blank"
                         draggable="false">
              <span>{{ item.purchaseInvoice.number }}</span>
            </router-link>
            <i v-else class="ti-minus"></i>
          </td>
          <td :class="{ 'new-item': isItemAdded(item.id) }">
            <span v-if="item.comment" @click.prevent href="" v-tooltip.top="item.comment"><i class="ti-comment-alt" ></i></span>
          </td>
          <td :class="{ 'new-item': isItemAdded(item.id) }" class="table-date-line">
            <span v-if="item.created_at || item.creator">{{ formatBy(item.created_at, item.creator) }}</span>
            <i v-else class="ti-minus"/>
          </td>
          <td :class="{'new-item': isItemAdded(item.id) }">
            <div class="actions">
              <AddButton v-if="!isItemAdded(item.id)" @click="addPosition(item)" :disabled="dataTableIsLoading || dataTableIsLoading  || isItemSelected(item.id) || disableAddPositionButton"/>
              <ReduceButton v-else @click="removePosition(item)" :disabled="dataTableIsLoading || dataTableIsLoading  || isItemSelected(item.id)"/>
            </div>
          </td>
        </tr>
      </template>
    </DesktopDataTable>

    <OwnPaginator v-show="!itemsArrayIsEmpty"
                  :rows="pagination.perPage"
                  :currentPage="pagination.currentPage - 1"
                  :totalPages="pagination.pageCount"
                  :totalRecords="pagination.totalCount"
                  :rowsPerPageOptions="[5,10,25,50,100,200]"
                  :pageLinkSize="11"
                  @page="changePage"
                  @change-per-page="changePerPage"
                  :changePaginationRoute="false"/>

    <TotalRecordsData :pagination="pagination" :itemsArrayIsEmpty="itemsArrayIsEmpty"/>

    <template #footer>
      <Button :label="$t('Close')" icon="pi pi-times" class="p-button-text" @click="close"/>
    </template>
  </Modal>

  <ConfirmMultipleDeleteModal
      :showSpinner="dataIsSending"
      :visible="confirmItemsMultipleDeleteModal"
      @close="closeConfirmItemsMultipleDeleteModal"
      @delete-items="deleteMultipleItems"/>

</template>

<script>
import settings from '@/settings'
import constants from '@/constants'
import httpClient from "@/services/http.services";
import formatMixins from '@/mixins/formatMixins'
import AddButton from '@/components/DataTable/AddButton'
import ReduceButton from '@/components/DataTable/ReduceButton'
import ConfirmMultipleDeleteModal from '@/components/ConfirmMultipleDeleteModal'
import ReducedPositionReasons from '@/translations/ReducedPositionReasons'
import ReducedPositionTypes from "@/translations/ReducedPositionTypes";
import httpMixins from "@/mixins/httpMixins";
import datatableMixins from "@/mixins/datatableMixins";
import OpenOrderButton from "@/components/OpenOrderButton.vue";


export default {
  mixins: [ formatMixins, httpMixins, datatableMixins ],
  components: {OpenOrderButton, AddButton, ReduceButton, ConfirmMultipleDeleteModal },
  // emits: ['close', 'update-items', 'add-position', 'remove-position', 'items-loaded', 'modal-data-on-clear'],
  emits: ['close', 'add-position', 'remove-position', 'disable-add-reduced-position-button', 'loading-complete'],
  name: 'ReducedPositionsModal',
  props: {
    visible: Boolean,
    removeReducedPositionId: [Number, null],
    supplierId: [Number, null],
  },
  data() {
    return {
      dataTableIsLoading: false,
      disableSearchInput: false,
      dataIsSending: false,
      // selectedType: null,
      // selectedTypeId: null,
      disableAddPositionButton: false,
      confirmItemsMultipleDeleteModal: false,
      isLoading: true,
      settings,
      submitted: false,
      fieldsExpand: 'position_data,order,car,creator,supplier,purchaseInvoicePosition,purchaseInvoice',
      apiLink: 'reduced-position',
      searchData: null,
      showOnlyLast30Days: true,
      sort: {
        column: 'id',
        direction: '',
      },
      items: [],
      addedItems: [],
      selectedItems: [],
      selectAllCheckbox: [],
      constants,
      ReducedPositionReasons,
      ReducedPositionTypes
    }
  },
  watch: {
    removeReducedPositionId(positionId) {
      if (!positionId) return false
      const positionIndex = this.addedItems.indexOf(positionId)
      if (positionIndex !== -1) {
        this.addedItems.splice(positionIndex, 1)
      }
    },
    selectedItems() {
      this.checkAllItemsAreSelected()
      // this.createSelectedInvoicesData()
    },
    '$store.state.thirdLayerIsOpened'() {
      if (!this.$store.state.thirdLayerIsOpened) {
        if (this.confirmItemsMultipleDeleteModal) {
          this.closeConfirmItemsMultipleDeleteModal()
        }
      }
    },
  },
  methods: {
    // clearModalData(clearAddedItems = true, clearSearchingData = true) {
    //   // this.selectedType = null
    //   // this.selectedTypeId = null
    //
    //   this.selectedItems = []
    //   this.selectAllCheckbox = []
    //   this.submitted = false
    //
    //   if (clearAddedItems) this.addedItems = []
    //   if (clearSearchingData) {
    //     this.searchData = ''
    //     this.showOnlyLast30Days = true
    //     this.$refs.searchInput?.clearLastSearchingValue()
    //   }
    //
    //   this.pagination = {
    //     totalCount: null,
    //     pageCount: null,
    //     currentPage: 1,
    //     perPage: 25
    //   }
    //   this.sort = {
    //     column: 'id',
    //     direction: '',
    //   }
    //   // this.$emit('modal-data-on-clear')
    // },
    clearModalData(clearAddedItems = true, clearSearchingData = true) {
      this.selectedItems = []
      this.selectAllCheckbox = []
      this.submitted = false

      if (clearAddedItems) {
        this.addedItems = []
      }

      const perPage = clearSearchingData
          ? 25
          : (this.pagination?.perPage ?? 25)

      if (clearSearchingData) {
        this.searchData = ''
        this.showOnlyLast30Days = true
        this.$refs.searchInput?.clearLastSearchingValue()

        this.sort = {
          column: 'id',
          direction: '',
        }
      }

      this.pagination = {
        totalCount: null,
        pageCount: null,
        currentPage: 1,
        perPage,
      }
    },
    addPosition(position) {
      if (!this.supplierId) {
        this.selectedItems = []
        this.selectAllCheckbox = []
      } else if (this.supplierId && position.supplier_id !== this.supplierId) {
        return
      }

      this.addedItems.push(position.id)

      const positionType = position.type === 1 ? 5 : 7

      this.$emit('add-position', position, positionType)
      this.checkAllItemsAreSelected()
    },
    removePosition(position) {
      const positionIndex = this.addedItems.indexOf(position.id)
      if (positionIndex !== -1) {
        this.addedItems.splice(positionIndex, 1)
        this.$emit('remove-position', position.id)
      }
      this.checkAllItemsAreSelected()
    },
    changePage(data) {
      if (this.pagination.currentPage === data.page + 1) return false
      this.pagination.currentPage = data.page + 1
      this.getItems()
    },
    changePerPage(perPage) {
      if (this.pagination.perPage === perPage) return false
      this.pagination.perPage = perPage
      this.pagination.currentPage = 1
      this.getItems()
    },
    changeLast30DaysFilter() {
      this.pagination.currentPage = 1
      this.selectedItems = []
      this.selectAllCheckbox = []
      this.getItems(true)
    },
    async getItems(showSpinner = false) {
      if (!this.userCanGetData || this.dataTableIsLoading) return
      // this.$store.commit('SET_FETCH_IN_FLIGHT', true)
      this.dataTableIsLoading = true

      if (showSpinner) this.isLoading = true

      const ctrl = this.setController?.('reducedPositionsModal')
      try {
        const { status, data, headers } = await httpClient({
          url: this.apiLink,
          // method: 'post',
          params: {
            supplierId: this.supplierId ?? null,
            // typeId: this.selectedTypeId,
            typeId: 1,
            page: this.pagination.currentPage,
            'per-page': this.pagination.perPage,
            sort: this.sort.direction + this.sort.column,
            sortColumn: this.sort.column,
            sortDirection: this.sort.direction,
            search: this.searchData,
            expand: this.fieldsExpand,
            show_only_unfixed: true,
            show_only_last_30_days: +this.showOnlyLast30Days,
          },
          signal: ctrl?.signal
        })
        if (status === 200 && data && Array.isArray(data)) {
          const hasPaginationHeaders = !!headers['x-pagination-total-count']
          if (hasPaginationHeaders) {
            this.pagination.totalCount = +headers['x-pagination-total-count']
            this.pagination.pageCount  = +headers['x-pagination-page-count']
          } else {
            this.pagination.totalCount = data.length
            this.pagination.pageCount  = 1
          }

          this.items = data

          const arrayIsEmpty = !data?.length

          if (arrayIsEmpty) {
            this.pagination.pageCount = 1
            this.pagination.currentPage = 1
          }

          const noItemsAtAll = arrayIsEmpty && !this.searchData && !this.showOnlyLast30Days
          this.$emit('disable-add-reduced-position-button', noItemsAtAll)
          this.disableSearchInput = noItemsAtAll
        } else {
          this.items = []
          this.showUnknownError()
        }
      } catch(err) {
        this.items = []
        this.showError(err)
      } finally {
        this.isLoading = false
        this.dataTableIsLoading = false
        this.checkAllItemsAreSelected()
        this.$emit('loading-complete')
        if (this._abortors?.reducedPositionsModal === ctrl) {
          this._abortors.reducedPositionsModal = null
        }
      }
    },
    async deleteMultipleItems() {
      this.dataIsSending = true
      try {
        const { status, data } = await httpClient.post('reduced-position/hide-positions', { ids: this.selectedItems })
        if (status === 200 && data?.success) {
          this.$toast.add({severity:'success', detail: this.$t('Data deleted'), life: this.settings.toastLife});
          this.searchData = null

          this.getItems()
          this.closeConfirmItemsMultipleDeleteModal()

          this.selectedItems = []
          this.selectAllCheckbox = []
        } else {
          this.showUnknownError()
        }
      } catch(err) {
        this.showError(err)
      } finally {
        this.dataIsSending = false
      }
    },
    confirmItemsMultipleDelete() {
      this.confirmItemsMultipleDeleteModal = true
      this.$store.commit('toggleThirdLayer', true)
    },
    closeConfirmItemsMultipleDeleteModal() {
      this.itemToDelete = {}
      this.confirmItemsMultipleDeleteModal = false
      this.$store.commit('toggleThirdLayer', false)
    },
    sortItems(columnName) { //Dont delete
      if (this.sort.column !== columnName) {
        this.sort.direction = ''
      } else {
        this.sort.direction = this.sort.direction === '' ? '-' : '';
      }
      this.sort.column = columnName
      this.getItems()
    },
    close() {
      this.$emit('close')
    }
  },
  computed: {
    allCurrentItemsAreAdded() {
      let result = true
      this.items.forEach(item => {
        if (!this.isItemAdded(item.id)) {
          result = false
        }
      })
      return result
    },
    actionButtonsCount() {
      return 1
    },
    computedWindowSize() {
      if (this.$store.state.mobileLayout) {
        return {position: 'absolute', top: '15px', width: '94%', marginTop: '10px'}
      } else if (!this.$store.state.mobileLayout && this.$store.state.tabletLayout) {
        return {position: 'absolute', top: '15px', width: '90%', marginTop: '15px'}
      } else {
        return {position: 'absolute', top: '15px', width: '87%', maxWidth: '1400px'}
      }
    },
  },

}
</script>

<style scoped lang="scss">

</style>